'use client';

import { useState } from 'react';
import ServiceCategoryCard from './ServiceCategoryCard';

interface Service {
  id: string;
  icon: 'BriefcaseIcon' | 'GlobeAltIcon' | 'UserGroupIcon' | 'AcademicCapIcon';
  title: string;
  description: string;
  features: string[];
}

const services: Service[] = [
  {
    id: 'exhibition-sales',
    icon: 'BriefcaseIcon',
    title: 'Exhibition Sales Management',
    description: 'End-to-end sales strategy and execution for trade shows and exhibitions. We transform your booth into a deal-closing machine.',
    features: [
      'Pre-exhibition sales strategy & goal setting',
      'Target buyer outreach & appointment setting',
      'Lead qualification frameworks & scoring',
      'On-stand sales coaching & deal facilitation',
      'Post-exhibition conversion tracking & follow-up',
      'CRM integration & pipeline management',
      'ROI reporting & performance analytics',
    ],
  },
  {
    id: 'market-entry',
    icon: 'GlobeAltIcon',
    title: 'Market Entry & Expansion',
    description: 'Strategic support for entering new international markets through exhibitions and business missions.',
    features: [
      'Go-to-market strategy for new countries',
      'Distributor & partner sourcing',
      'Country-specific positioning & messaging',
      'Pricing & offer adaptation',
      'Regulatory & compliance guidance',
      'Local market intelligence & competitor analysis',
      'Cultural adaptation & communication strategies',
    ],
  },
  {
    id: 'representation',
    icon: 'UserGroupIcon',
    title: 'Exhibition Representation',
    description: 'Local sales representation and B2B matchmaking services at exhibitions and trade shows.',
    features: [
      'Local sales representation at exhibitions',
      'National pavilion sales support',
      'B2B matchmaking services',
      'VIP & C-level meeting coordination',
      'Multilingual sales support (English, Russian, Arabic)',
      'On-site negotiation & deal structuring',
      'Post-event relationship management',
    ],
  },
  {
    id: 'consulting',
    icon: 'AcademicCapIcon',
    title: 'Consulting & Training',
    description: 'Expert guidance to optimize exhibition ROI and build internal sales capabilities.',
    features: [
      'Exhibition ROI optimization audits',
      'Sales team training for exhibitions',
      'Consultative & emotional intelligence–based sales',
      'Post-event analysis & reporting',
      'Long-term partnership development strategies',
      'Custom sales playbooks for exhibitions',
      'Ongoing coaching & performance reviews',
    ],
  },
];

export default function ServicesInteractive() {
  const [expandedService, setExpandedService] = useState<string | null>(null);

  const handleToggle = (serviceId: string) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  return (
    <section className="section-padding bg-background">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fadeIn">
          <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              Service Breakdown
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground">
            What's Included in Each Service
          </h2>
          <p className="text-lg text-muted-foreground mt-4 max-w-2xl mx-auto">
            Click on any service to expand and see detailed deliverables.
          </p>
        </div>

        {/* Service Cards */}
        <div className="space-y-6 max-w-5xl mx-auto">
          {services.map((service, index) => (
            <div
              key={service.id}
              className="animate-fadeIn"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <ServiceCategoryCard
                icon={service.icon}
                title={service.title}
                description={service.description}
                features={service.features}
                isExpanded={expandedService === service.id}
                onToggle={() => handleToggle(service.id)}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}